import { useMemo } from 'react';
import { useDensityModeContext, type DensityMode } from '@/contexts/DensityModeContext';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook para gerenciar modo de densidade POR USUÁRIO + CONTEXTO (módulo/setor)
 * Cada usuário tem preferências independentes para cada contexto
 * 
 * Persistência: localStorage com chave `density-{userId}-{contextKey}`
 * Valor padrão: 'comfortable' (primeiro acesso)
 * 
 * @param contextKey - Chave única do contexto (ex: 'manager', 'operador-IMPRESSAO', 'arte')
 * 
 * @example
 * // No ManagerDashboard:
 * const { densityMode, cycleDensityMode } = useDensityMode('manager');
 * 
 * // No OperatorDashboard para setor IMPRESSAO:
 * const { densityMode } = useDensityMode('operador-IMPRESSAO');
 * 
 * // No módulo Arte:
 * const { densityMode, setDensityMode } = useDensityMode('arte');
 */
export function useDensityMode(contextKey: string) {
  const ctx = useDensityModeContext();
  const { user } = useAuth();
  
  // userId do usuário logado ou 'anonymous' como fallback
  const userId = user?.id || 'anonymous';
  
  // Ler valor atual do contexto (reativo - densityCache é estado React)
  // O useMemo garante recálculo quando cache ou userId/contextKey mudam
  const densityMode = useMemo(() => {
    return ctx.getDensity(userId, contextKey);
  }, [ctx, ctx.densityCache, userId, contextKey]);
  
  // Funções memoizadas para evitar recriação
  const helpers = useMemo(() => ({
    setDensityMode: (mode: DensityMode) => ctx.setDensity(userId, contextKey, mode),
    cycleDensityMode: () => ctx.cycleDensity(userId, contextKey),
    getDensityLabel: () => ctx.getLabelForMode(densityMode),
    getDensityIcon: () => ctx.getIconForMode(densityMode),
    isCompactMode: densityMode === 'compact' || densityMode === 'ultra-compact',
    isUltraCompactMode: densityMode === 'ultra-compact'
  }), [ctx, userId, contextKey, densityMode]);

  return {
    densityMode,
    ...helpers
  };
}

// Re-exportar tipo para retrocompatibilidade
export type { DensityMode };
